import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { closeRulesModal } from '@/store/slices/modalSlice';
import useOutsideClick from '@/hooks/useOutsideClick';
import useModalAnimation from '@/hooks/useModalAnimation';
import { Icons } from '@/utils/icons';
import { rulesData } from '@/utils/constants/rules';

export default function RulesModal() {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.modal.isRulesModalOpen);
  const modalRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [openSport, setOpenSport] = useState<string>('Football');
  const [openCategory, setOpenCategory] = useState<string>('');
  const { animate, handleClose } = useModalAnimation({
    isOpen,
    onClose: () => dispatch(closeRulesModal()),
  });

  useOutsideClick(modalRef, handleClose);

  const languages = useMemo(
    () => [
      {
        id: 'en',
        label: 'English',
        flag: 'https://wver.sprintstaticdata.com/v201/static/front/img/flag_english.png',
      },
    ],
    []
  );
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);

  const toggleSport = (sport: string) => {
    setOpenSport((prev) => (prev === sport ? '' : sport));
    setOpenCategory('');
  };

  const toggleCategory = (key: string) => {
    setOpenCategory((prev) => (prev === key ? '' : key));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 font-roboto">
      <div
        ref={modalRef}
        className={`w-full md:max-w-[500px] lg:max-w-[800px] max-h-[90vh] bg-white flex flex-col ${animate ? 'translate-y-0 opacity-100' : '-translate-y-6 opacity-0'} transform transition-all duration-300 ease-out`}
      >
        {/* Header */}
        <div className="bg-primary px-2 py-2.5 flex items-center justify-between">
          <h2 className="text-black font-medium text-xl">Rules</h2>
        </div>

        {/* Body */}
        <div className="h-full p-2 pb-0 flex-1 flex flex-col overflow-y-auto">
          <div ref={dropdownRef} className="min-w-[100px] ml-auto relative">
            <button
              type="button"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              className="flex items-center gap-2 rounded-sm bg-white px-2 py-1 text-sm"
            >
              <img
                src={selectedLanguage?.flag}
                alt={selectedLanguage?.label}
                className="w-7 object-cover rounded-[2px]"
              />
              {/* <span className="text-black">{selectedLanguage?.label}</span> */}
              {/* <Icons.solidDown className="text-xs text-black -mt-1" /> */}
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-1 w-[180px] overflow-hidden rounded-sm bg-white shadow-md">
                {languages.map((lang) => (
                  <button
                    key={lang.id}
                    type="button"
                    onClick={() => {
                      setSelectedLanguage(lang);
                      setIsDropdownOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-2 py-2 text-left text-sm text-white bg-emerald-700"
                  >
                    <img
                      src={lang.flag}
                      alt={lang.label}
                      className="w-7 object-cover rounded-[2px]"
                    />
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {rulesData.map((sportItem, si) => {
              const isOpen = openSport === sportItem.sport;
              return (
                <div key={si} className="">
                  {/* Sport header */}
                  <div
                    onClick={() => toggleSport(sportItem.sport)}
                    className={`px-1 py-[5px] cursor-pointer flex items-center gap-2.5 select-none`}
                  >
                    <span
                      className={`h-4 w-4 rounded-full flex items-center justify-center text-white text-10 bg-primary-dark-grey`}
                    >
                      {isOpen ? <Icons.minusBold /> : <Icons.plusBold />}
                    </span>
                    <span className="text-sm text-black">{sportItem.sport}</span>
                  </div>

                  {/* Categories */}
                  {isOpen && (
                    <div>
                      {sportItem.categories.map((cat, ci) => {
                        const catKey = `${sportItem.sport}-${cat.name}-${ci}`;
                        const catOpen = openCategory === catKey;

                        return (
                          <div key={ci} className="">
                            <div
                              onClick={() => toggleCategory(catKey)}
                              className={`pl-4 pr-4 py-[3px] cursor-pointer flex items-center gap-0.5 bg-[#FDFBFE] select-none`}
                            >
                              <span className="h-4 w-4 rounded-full bg-black flex items-center justify-center text-white text-[10px] font-bold">
                                {catOpen ? <Icons.minusBold /> : <Icons.plusBold />}
                              </span>
                              <span className="text-sm text-black px-1 py-0.5">{cat.name}</span>
                            </div>

                            {catOpen && (
                              <div className="pl-[52px] pr-4 py-2 bg-[#FDFBFE]">
                                {cat.rules.map((rule, ri) => (
                                  <div
                                    key={ri}
                                    className={`py-1.5 text-sm leading-[22px] ${rule.danger ? 'text-[#ce0c1c] font-bold' : 'text-primary-dark-grey'}`}
                                  >
                                    {rule.text}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-2 pt-0">
          <div className="p-2 bg-[#4b4b4b] text-white px-4 py-2.5 flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm">I agree all rules</p>
            <div className="flex items-center gap-2 font-arial text-base">
              <button
                onClick={handleClose}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-[7px]"
              >
                Cancel
              </button>
              <button
                onClick={() => agreed && handleClose()}
                className={`px-4 py-[7px] text-white bg-green-600 hover:bg-green-700`}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
